import {
  addCalendarDays,
  newId,
  type Day,
  type LeftoverRef,
  type MealPlan,
  type MealPlanApiItem,
  type Slot,
} from "@recipevault/shared";

/**
 * Rewrite a copied slot's `leftoverOf` against the whole-week old-id -> new-id map.
 *
 * - Resolves within this week -> rewritten to the new id (label, if any, kept alongside).
 * - Doesn't resolve (e.g. it pointed at a PRIOR week's slot, per the design's tolerance for
 *   a `leftoverOf.slotId` that isn't addressable within the current week) but has a label
 *   -> keep the label, drop the now-meaningless id.
 * - Doesn't resolve and has no label -> nothing safe to fall back on; leave the (now
 *   dangling) id as-is rather than strip it — `LeftoverRefSchema` requires at least one of
 *   `slotId`/`label`, so dropping the id here would produce an invalid ref.
 */
function remapLeftover(ref: LeftoverRef | undefined, idMap: Map<string, string>): LeftoverRef | undefined {
  if (!ref) return undefined;
  if (ref.slotId === undefined) return { ...ref }; // label-only ref — nothing to remap
  const remapped = idMap.get(ref.slotId);
  if (remapped !== undefined) return { ...ref, slotId: remapped };
  if (ref.label !== undefined) return { label: ref.label };
  return { ...ref };
}

/**
 * Pure composition for "duplicate a prior week": copies `source`'s days (dishes + notes)
 * into a fresh week starting at `targetWeekStart`, rebasing day *i* of the source to
 * `targetWeekStart + i` days.
 *
 * Every slot gets a FRESH id (`newId("slot")`) — a source slot's id belongs to a different
 * week's row and must never be reused — and `skipped` resets to `false` (a skip decision
 * from the source week carries no meaning for the new one). `leftoverOf.slotId` is
 * remapped through the fresh-id map (see `remapLeftover`). Tasks are NEVER copied —
 * lead-time tasks are specific to their own week's dates, so the duplicated week always
 * starts with an empty task list.
 *
 * Returns a full `MealPlanApiItem`-shaped body (stamped with `userId`/`now`, mirroring
 * `buildEmptyWeek`'s return shape) rather than the narrower `CreateMealPlanRequest` wire
 * type: `CreateMealPlanRequestSchema` is `.strict()` server-side, so a caller that POSTs
 * this (`DuplicateWeekSheet`) must pick just `{ weekStart, days, tasks, cookingFor }` off
 * this result before sending it — the server re-derives `createdAt`/`createdBy` etc.
 * itself regardless. The stamped fields exist so this composition is independently a
 * fully-formed row (e.g. usable for an optimistic Dexie write) without recomputing them.
 */
export function duplicateWeek(
  source: MealPlan,
  targetWeekStart: string,
  userId: string,
  now: string,
): MealPlanApiItem {
  // Build the whole-week id map FIRST (not per-day) so a leftoverOf pointing at a slot on
  // a *different* day within the same week still resolves regardless of iteration order.
  const idMap = new Map<string, string>();
  for (const day of source.days) {
    for (const slot of day.slots) {
      idMap.set(slot.id, newId("slot"));
    }
  }

  const days: Day[] = source.days.map((day, i) => {
    const slots: Slot[] = day.slots.map((slot) => {
      const newSlot: Slot = {
        ...slot,
        // Guaranteed present: idMap was built from this exact same source.days traversal.
        id: idMap.get(slot.id)!,
        items: slot.items.map((item) => ({ ...item })),
        skipped: false,
      };
      const remapped = remapLeftover(slot.leftoverOf, idMap);
      if (remapped) {
        newSlot.leftoverOf = remapped;
      } else {
        delete newSlot.leftoverOf;
      }
      return newSlot;
    });
    return { ...day, date: addCalendarDays(targetWeekStart, i), slots };
  });

  return {
    schemaVersion: 1,
    weekStartDate: targetWeekStart,
    days,
    tasks: [],
    createdAt: now,
    updatedAt: now,
    createdBy: userId,
    updatedBy: userId,
    deletedAt: null,
    ...(source.cookingFor !== undefined ? { cookingFor: source.cookingFor } : {}),
  };
}
